import React from "react";
import { Sun } from "lucide-react";
import { PageHeader, Section, ButtonLink } from "../components/ui-kit";
import { DayTimeline } from "../components/DayTimeline";

const WEEK = [
  {
    day: "Monday",
    morning: "Cooking session — preparing and making food together.",
    afternoon: "Meaningful activities such as arts and crafts.",
  },
  {
    day: "Tuesday",
    morning: "Accessing the community, visiting local places together.",
    afternoon: "Accessing essential life skills such as cooking, and being supported to gain a qualification in Food Hygiene Level 2.",
  },
  {
    day: "Wednesday",
    morning: "Swimming at the local pool.",
    afternoon: "A sociable trip to the pub.",
  },
  {
    day: "Thursday",
    morning: "Various sporting activities such as football and basketball.",
    afternoon: "Performing arts with our regular dance instructor.",
  },
  {
    day: "Friday",
    morning: "Hockey and a variety of other activities.",
    afternoon: "A relaxed, chilled afternoon to round off the week.",
  },
];

export default function TypicalWeek() {
  return (
    <>
      <PageHeader
        eyebrow="A typical week"
        title="Our weekly timetable"
        intro="Here is how a typical week looks at Spire Inclusion. Days are still shaped around the people we support, so there is always flexibility — but this is the usual rhythm of our week."
        testid="typical-week-header"
      />
      <Section tone="default">
        <div className="space-y-6">
          {WEEK.map((d) => (
            <div
              key={d.day}
              className="rounded-2xl bg-white border border-stone-200 p-6 sm:p-8"
              data-testid={`typical-week-${d.day.toLowerCase()}`}
            >
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
                  <Sun className="h-6 w-6" aria-hidden="true" />
                </span>
                <h2 className="font-heading text-2xl font-semibold text-stone-900">{d.day}</h2>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl bg-stone-50 p-5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-orange-700">Morning</p>
                  <p className="mt-1 text-lg text-stone-700">{d.morning}</p>
                </div>
                <div className="rounded-xl bg-stone-50 p-5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-orange-700">Afternoon</p>
                  <p className="mt-1 text-lg text-stone-700">{d.afternoon}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="white" data-testid="day-at-spire">
        <div className="max-w-2xl">
          <p className="text-base font-semibold uppercase tracking-wide text-orange-700 mb-3">A day at Spire</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
            From hello to home time
          </h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            Here's how a typical day flows — always flexible, always shaped around the people we support.
          </p>
        </div>
        <DayTimeline />
      </Section>

      <Section tone="tinted">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">Built around the person</h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            Some people thrive with a busy week, others prefer a calmer pace. We adjust the balance
            of activity, rest and routine so each person feels comfortable and engaged.
          </p>
          <ButtonLink to="/contact" className="mt-8" data-testid="typical-week-contact-button">
            Ask us about a week with us
          </ButtonLink>
        </div>
      </Section>
    </>
  );
}
